import { InfoIcon } from "@phosphor-icons/react";
import {
  RECOVERY_EFFORT_ONGOING,
  RECOVERY_PLAN_URL,
} from "@constants/upgradeFlags";
import useDepositPoolPaused from "@hooks/useDepositPoolPaused";
import useWithdrawalManagerPaused from "@hooks/useWithdrawalManagerPaused";

export type RecoveryNoticeContext = "deposit" | "withdrawal";

interface RecoveryNoticeProps {
  className?: string;
  context?: RecoveryNoticeContext;
}

const RECOVERY_COPY: Record<RecoveryNoticeContext, string> = {
  deposit: "rsETH deposits are paused as part of the ongoing recovery effort.",
  withdrawal:
    "rsETH withdrawals are paused as part of the ongoing recovery effort.",
};

const MAINTENANCE_COPY: Record<RecoveryNoticeContext, string> = {
  deposit:
    "rsETH deposits are temporarily paused for scheduled maintenance and will resume shortly.",
  withdrawal:
    "rsETH withdrawals are temporarily paused for scheduled maintenance and will resume shortly.",
};

const RecoveryNotice = ({
  className = "",
  context = "withdrawal",
}: RecoveryNoticeProps) => {
  const { isPaused: isDepositPoolPaused } = useDepositPoolPaused();
  const { isPaused: isWithdrawalManagerPaused } = useWithdrawalManagerPaused();
  const isPaused =
    context === "deposit" ? isDepositPoolPaused : isWithdrawalManagerPaused;

  if (!isPaused) return null;

  const isRecovery = RECOVERY_EFFORT_ONGOING;
  const message = isRecovery
    ? RECOVERY_COPY[context]
    : MAINTENANCE_COPY[context];

  return (
    <div
      className={`flex items-start gap-2 rounded-lg bg-[#fffaee] p-3 ${className}`}
      role="status"
    >
      <span className="shrink-0 mt-px inline-flex">
        <InfoIcon size={19} weight="fill" color="#a67d00" />
      </span>
      <div className="flex flex-col gap-1 text-[12px] leading-[1.4]">
        <p className="text-[#1a1a1a] m-0">{message}</p>
        {isRecovery && (
          <a
            href={RECOVERY_PLAN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#a67d00] underline underline-offset-2 decoration-solid hover:opacity-80"
          >
            {"Read the full recovery plan →"}
          </a>
        )}
      </div>
    </div>
  );
};

export default RecoveryNotice;
